import { DataType } from "@dndModels/Types";

// Builds a "where used" index for Floating Features and Creature Features.
//
// Rather than knowing every model that can hold a Feature, this walks the DB
// shape (toDBObj) of each object looking for the two places a feature id can
// live:
//   - Feature.floating_feature_id (inside any FeatureBase.features, or
//     SummonUpgradeFeature.features)
//   - EffectBase.sf_feature.ff_id ("Special Feature" effects)
// That covers Feature Sources, Creatures, Creature Gifts, Optional Feature Bases,
// and Summon custom stat blocks / Transform options nested in effects of Spells,
// Abilities, Creature Abilities, etc.
//
// Summon/Transform creature_ids and built_in_gift_ids are NOT counted as uses of
// the creature's/gift's features - they're recorded separately as links so the
// UI can show "Summoned by X" next to a Creature (or Creature Gift) user.

export interface FeatureUsage {
  user_type: DataType;
  user_id: string;
  user_name: string;
  path: string[];
  via: "feature" | "special_feature_effect";
  source_book_ids: string[];
}

export interface ObjectLink {
  from_type: DataType;
  from_id: string;
  from_name: string;
  path: string[];
  kind: "creature" | "creature_gift";
}

export interface FeatureUsageIndex {
  // Keyed by the canonical feature id (merged ids are folded into the id they were merged into).
  usages: Map<string, FeatureUsage[]>;
  // Keyed by creature / creature_gift id.
  links: Map<string, ObjectLink[]>;
}

export interface UsageSourceObject {
  _id: string;
  name?: string;
  true_name?: string;
  source_books_old?: Array<{ id: string }>;
  source_books?: Array<{ id?: string, _id?: string }>;
  toDBObj?: () => any;
}

const sourceBookIdsOf = (obj: UsageSourceObject): string[] => {
  const ids = obj.source_books_old && obj.source_books_old.length > 0 ?
    obj.source_books_old.map(o => o.id) :
    (obj.source_books ?? []).map(o => o.id ?? o._id ?? "");
  return ids.filter((id, i) => id !== "" && ids.indexOf(id) === i);
};

export interface AliasableFeature {
  _id: string;
  merged_ids?: string[];
}

// Runtime-only fields that would either cycle or double count. old_obj isn't
// skipped: in stored documents it's a real saved reference, and counting it
// errs on the side of keeping a feature.
const SKIP_KEYS = new Set(["floating_feature", "the_conditions", "single_ff"]);

export function buildFeatureAliases(features: AliasableFeature[]): Map<string, string> {
  const aliases = new Map<string, string>();
  for (const f of features) {
    for (const merged_id of f.merged_ids ?? []) {
      aliases.set(merged_id, f._id);
    }
  }
  return aliases;
}

export function buildFeatureUsageIndex(
  sources: Array<{ data_type: DataType, objects: UsageSourceObject[] }>,
  aliases: Map<string, string> = new Map()
): FeatureUsageIndex {
  const usages = new Map<string, FeatureUsage[]>();
  const links = new Map<string, ObjectLink[]>();

  const push = <T>(map: Map<string, T[]>, key: string, value: T) => {
    const list = map.get(key);
    if (list) {
      list.push(value);
    } else {
      map.set(key, [value]);
    }
  };

  for (const { data_type, objects } of sources) {
    for (const obj of objects) {
      const user_name = obj.true_name || obj.name || obj._id;
      const source_book_ids = sourceBookIdsOf(obj);
      const db = typeof obj.toDBObj === "function" ? obj.toDBObj() : obj;
      const seen = new WeakSet<object>();

      const addUsage = (ff_id: string, path: string[], via: FeatureUsage["via"]) => {
        const id = aliases.get(ff_id) ?? ff_id;
        if (id === obj._id) {
          return; // A feature mentioning itself isn't a use.
        }
        const existing = usages.get(id);
        const path_str = path.join("\u0000");
        if (existing && existing.some(u => u.user_id === obj._id && u.via === via && u.path.join("\u0000") === path_str)) {
          return;
        }
        push(usages, id, { user_type: data_type, user_id: obj._id, user_name, path, via, source_book_ids });
      };

      const walk = (node: any, path: string[]) => {
        if (!node || typeof node !== "object" || seen.has(node)) {
          return;
        }
        seen.add(node);
        if (Array.isArray(node)) {
          node.forEach(item => walk(item, path));
          return;
        }
        if (typeof node.floating_feature_id === "string" && node.floating_feature_id !== "") {
          addUsage(node.floating_feature_id, path, "feature");
        }
        for (const key of Object.keys(node)) {
          if (SKIP_KEYS.has(key)) {
            continue;
          }
          const value = node[key];
          if (key === "sf_feature") {
            if (value && typeof value.ff_id === "string" && value.ff_id !== "") {
              addUsage(value.ff_id, path, "special_feature_effect");
            }
            continue;
          }
          if ((key === "creature_ids" || key === "built_in_gift_ids") && Array.isArray(value)) {
            const kind = key === "creature_ids" ? "creature" : "creature_gift";
            for (const id of value) {
              if (typeof id === "string" && id !== "") {
                push(links, id, { from_type: data_type, from_id: obj._id, from_name: user_name, path, kind });
              }
            }
            continue;
          }
          if (Array.isArray(value)) {
            value.forEach((item, i) => walk(item, [...path, ...crumbFor(key, item, i, value.length)]));
          } else if (value && typeof value === "object") {
            walk(value, [...path, ...crumbFor(key, value, 0, 1)]);
          }
        }
      };

      walk(db, []);
    }
  }

  return { usages, links };
}

const isFeatureBaseShape = (item: any) =>
  item && typeof item === "object" && Array.isArray(item.features) && item.floating_feature_id === undefined;

// Human-readable breadcrumb for stepping into node[key][i]. Returns [] for
// structural steps that don't add information.
function crumbFor(key: string, item: any, i: number, count: number): string[] {
  const named = (label: string) => item && item.name ? `${label} "${item.name}"` : `${label} ${i + 1}`;
  switch (key) {
    case "effects":
      return [named("Effect")];
    case "potences":
      return count > 1 ? [`Potence ${i + 1}`] : [];
    case "summon_options":
      return [item?.custom_stat_block?.name ? `Summon "${item.custom_stat_block.name}"` : `Summon (${item?.summon_type ?? "?"})`];
    case "transform_options":
      return [named("Transform")];
    case "custom_stat_block":
    case "features":
      return [];
  }
  if (isFeatureBaseShape(item)) {
    const parts: string[] = [];
    if (item.creature_fb_type) {
      parts.push(item.creature_fb_type);
    }
    if (item.level) {
      parts.push(`Level ${item.level}`);
    }
    parts.push(item.name ? `"${item.name}"` : `Feature Base ${i + 1}`);
    return [parts.join(" ")];
  }
  return [];
}

// Source books a feature appears in through its users. Floating/Creature Features
// get a placeholder source book on load, so when the user is itself a feature,
// its own users' books are used instead (recursively, cycle safe).
export function buildFeatureSourceBooks(
  index: FeatureUsageIndex,
  feature_types: DataType[] = ["floating_feature", "creature_feature"]
): Map<string, string[]> {
  const memo = new Map<string, string[]>();
  const resolve = (feature_id: string, visiting: Set<string>): string[] => {
    const cached = memo.get(feature_id);
    if (cached) {
      return cached;
    }
    visiting.add(feature_id);
    const ids = new Set<string>();
    for (const u of index.usages.get(feature_id) ?? []) {
      if (feature_types.includes(u.user_type)) {
        if (!visiting.has(u.user_id)) {
          resolve(u.user_id, visiting).forEach(id => ids.add(id));
        }
      } else {
        u.source_book_ids.forEach(id => ids.add(id));
      }
    }
    visiting.delete(feature_id);
    const result = Array.from(ids);
    // Results computed mid-cycle may be incomplete, so only cache from the top level.
    if (visiting.size === 0) {
      memo.set(feature_id, result);
    }
    return result;
  };
  const result = new Map<string, string[]>();
  index.usages.forEach((_, feature_id) => {
    result.set(feature_id, resolve(feature_id, new Set()));
  });
  return result;
}

export function describeUsagePath(usage: FeatureUsage): string {
  const via = usage.via === "special_feature_effect" ? " (Special Feature effect)" : "";
  return usage.path.length > 0 ? `${usage.path.join(" → ")}${via}` : via.trim();
}
